import { SPEEDS } from '../lib/constants';
import { markAnswer, type LetterMark } from '../lib/practice';
import type { Speed } from '../lib/types';

/**
 * The drill state machine. Like the chat reducer it is pure — the hook owns
 * the clock and the word choice, this owns everything decidable.
 *
 *   ready → signing → answer → feedback → signing → …
 *                ↑________replay________↓
 */

export type PracticePhase = 'ready' | 'signing' | 'answer' | 'feedback';

export interface PracticeState {
  phase: PracticePhase;
  /** The word being spelled, uppercase. Empty until the first round starts. */
  word: string;
  answer: string;
  /** True once the current word has been submitted — a replay then returns to feedback. */
  submitted: boolean;
  speed: Speed;
  /** Consecutive fully-correct words. Three in a row steps the speed up. */
  streak: number;
  best: number;
  /** Consecutive words with any error. Two in a row steps the speed down. */
  misses: number;
  wordsDone: number;
  lettersRight: number;
  lettersTotal: number;
  /** Replays used on the current word. */
  replays: number;
  /** Bumped whenever spelling (re)starts, so the timer re-arms. */
  run: number;
}

export type PracticeAction =
  | { type: 'start'; word: string }
  | { type: 'shown' }
  | { type: 'setAnswer'; answer: string }
  | { type: 'submit' }
  | { type: 'replay' }
  | { type: 'next'; word: string }
  | { type: 'setSpeed'; speed: Speed };

export const initialPracticeState: PracticeState = {
  phase: 'ready',
  word: '',
  answer: '',
  submitted: false,
  speed: 0.5,
  streak: 0,
  best: 0,
  misses: 0,
  wordsDone: 0,
  lettersRight: 0,
  lettersTotal: 0,
  replays: 0,
  run: 0,
};

function beginWord(state: PracticeState, word: string): PracticeState {
  return {
    ...state,
    phase: 'signing',
    word,
    answer: '',
    submitted: false,
    replays: 0,
    run: state.run + 1,
  };
}

function stepSpeed(speed: Speed, direction: 1 | -1): Speed {
  const index = SPEEDS.indexOf(speed) + direction;
  return SPEEDS[Math.min(SPEEDS.length - 1, Math.max(0, index))] ?? speed;
}

export function practiceReducer(state: PracticeState, action: PracticeAction): PracticeState {
  switch (action.type) {
    case 'start':
      return state.phase === 'ready' ? beginWord(state, action.word) : state;

    case 'shown':
      // The spelling timer fired. A stale timer outside `signing` means nothing.
      if (state.phase !== 'signing') return state;
      return { ...state, phase: state.submitted ? 'feedback' : 'answer' };

    case 'setAnswer':
      return state.phase === 'answer' ? { ...state, answer: action.answer } : state;

    case 'submit': {
      if (state.phase !== 'answer') return state;
      const marks = markAnswer(state.word, state.answer);
      const right = marks.filter((mark) => mark.ok).length;
      // Every mark must be clean, not just as many as the word is long —
      // otherwise answering SAM with SAMANTHA scores a perfect read.
      const perfect = state.word.length > 0 && marks.every((mark) => mark.ok);

      const streak = perfect ? state.streak + 1 : 0;
      const misses = perfect ? 0 : state.misses + 1;
      // Three clean reads step the speed up; two misses running step it down.
      let speed = state.speed;
      let missesOut = misses;
      if (perfect && streak % 3 === 0) speed = stepSpeed(speed, 1);
      if (!perfect && misses >= 2) {
        speed = stepSpeed(speed, -1);
        missesOut = 0;
      }

      return {
        ...state,
        phase: 'feedback',
        submitted: true,
        streak,
        best: Math.max(state.best, streak),
        misses: missesOut,
        speed,
        wordsDone: state.wordsDone + 1,
        lettersRight: state.lettersRight + right,
        lettersTotal: state.lettersTotal + state.word.length,
      };
    }

    case 'replay':
      // Watch the word again — from the ask (before answering) or from feedback.
      if (state.phase !== 'answer' && state.phase !== 'feedback') return state;
      return { ...state, phase: 'signing', replays: state.replays + 1, run: state.run + 1 };

    case 'next':
      return state.phase === 'feedback' ? beginWord(state, action.word) : state;

    case 'setSpeed':
      return { ...state, speed: action.speed };

    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

/* ── selectors ─────────────────────────────────────────────────────────── */

export function currentMarks(state: PracticeState): LetterMark[] {
  return markAnswer(state.word, state.answer);
}

export function readPerfect(state: PracticeState): boolean {
  return state.word.length > 0 && currentMarks(state).every((mark) => mark.ok);
}

/** Session accuracy in percent, or null before any word is scored. */
export function accuracyPercent(state: PracticeState): number | null {
  if (!state.lettersTotal) return null;
  return Math.round((state.lettersRight / state.lettersTotal) * 100);
}
